import React, {
    useCallback, useContext, useEffect, useMemo, useState 
} from 'react';
import {
    Box, 
    Typography, 
    useTheme
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import Pillar from './pillar';
import LottieAnimation from './lottie';
import dataDogLogger from '../../../../lib/datadog';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';
import {
    useCookies 
} from 'react-cookie';

type Props = {
    position: number
};

export const Pillars2 = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const drinksBaseline = useMemo(() => {
        return formContext.answers.totalDrinksBaseline;
    }, [formContext.answers.totalDrinksBaseline]);
    const dryDaysBaseline = useMemo(() => {
        return formContext.answers.totalDryDaysBaseline;
    }, [formContext.answers.totalDryDaysBaseline]);
    const concernLevelAnswer = useMemo(() => {
        return formContext.answers['concern_level'];
    }, [formContext.answers['concern_level']]);
    const drinkBaselines = useMemo(() => {
        const answers = formContext.answers;
        return [
            answers.mon_baseline,
            answers.tue_baseline,
            answers.wed_baseline,
            answers.thu_baseline,
            answers.fri_baseline,
            answers.sat_baseline,
            answers.sun_baseline
        ];
    }, [
        formContext.answers.mon_baseline,
        formContext.answers.tue_baseline,
        formContext.answers.wed_baseline,
        formContext.answers.thu_baseline,
        formContext.answers.fri_baseline,
        formContext.answers.sat_baseline,
        formContext.answers.sun_baseline
    ]);
    const forwardJump = useMemo(() => {
        return formContext.answers?.totalDrinksBaseline === 0 ? 4 : 1;
    }, [formContext.answers?.totalDrinksBaseline]);
    const initialScreenWidth = useMemo(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth;
        }
        return 0;
    }, []);
    const [screenWidth, setScreenWidth] = useState(initialScreenWidth);

    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflowY: 'scroll',
            overflowX: 'hidden',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
        },
        progress: {
            marginBottom: '24px',
            transition: 'transform 0.5s'
        },
        image: {
            zIndex: 1,
            width: '168px',
            height: '168px',
            borderRadius: '50%',
            border: `8px solid ${theme.palette.white}`,
        },
        texts: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        text1: {
            width: '311px',
            height: '72px',
            transform: 'translate(0, 50px)',
            opacity: 0,
            animation: '$slideUp 0.5s forwards 0.5s, $fadeIn 0.5s forwards 0.5s',
        },
        text2: {
            width: '154px',
            height: '62px',
            transform: 'translate(78px, 50px)',
            opacity: 0,
            animation: '$slideUp2 0.5s forwards 1s, $fadeIn 0.5s forwards 1s',
        },
        text3: {
            width: '311px',
            height: '56px',
            transform: 'translate(0, 50px)',
            opacity: 0,
            animation: '$slideUp 0.5s forwards 1.5s, $fadeIn 0.5s forwards 1.5s',
        },
        coaches: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
        },
        hannah: {
            position: 'absolute',
            width: '150px',
            height: '150px',
            transform: 'translate(55px, -70px) scale(0)',
            animation: '$grow1 0.5s forwards 0.5s',
        },
        ash: {
            width: '124px',
            height: '124px',
            transform: 'translate(-55px, 20px) scale(0)',
            animation: '$grow2 0.5s forwards 1s',
        },
        cairo: {
            width: '138px',
            height: '138px',
            transform: 'translate(0, -25px) scale(0)',
            animation: '$grow3 0.5s forwards 1.5s',
        },
        snake: {
            animation: '$snake 2s forwards 0s',
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes slideUp': {
            '0%': {
                transform: 'translate(0, 50px)' 
            },
            '100%': {
                transform: 'translate(0)' 
            },
        },
        '@keyframes slideUp2': {
            '0%': {
                transform: 'translate(78px, 50px)' 
            },
            '100%': {
                transform: 'translate(78px, 0)' 
            },
        },
        '@keyframes grow1': {
            '0%': {
                transform: 'translate(55px, -70px) scale(0)' 
            },
            '100%': {
                transform: 'translate(55px, -70px) scale(1)' 
            },
        },
        '@keyframes grow2': {
            '0%': {
                transform: 'translate(-55px, 20px) scale(0)' 
            },
            '100%': {
                transform: 'translate(-55px, 20px) scale(1)' 
            },
        },
        '@keyframes grow3': {
            '0%': {
                transform: 'translate(0, -25px) scale(0)' 
            },
            '100%': {
                transform: 'translate(0, -25px) scale(1)' 
            },
        },
        '@keyframes snake': {
            '0%': {
                strokeDashoffset: Math.min(screenWidth * 7 / 2, 2422) - Math.min(screenWidth / 2, 346) 
            },
            '100%': {
                strokeDashoffset: 0 
            },
        },
    })), [screenWidth]);

    const classes = useStyles();
    const theme = useTheme();
    const [cookies] = useCookies();
    const [startTime, setStartTime] = useState(formContext.startTime);
    const [step, setStep] = useState(formContext.screenDirection === 'right' ? 3 : 0);
    const [direction, setDirection] = useState(formContext.screenDirection);

    useEffect(() => {
        if (step > 3 || step === -1) return;

        formContext.fireTrackingEvents(formContext, screens[step].props.trackingName, cookies);
    }, [step]);

    const advanceScreen = () => {
        if (step === 3) return formContext.goNext(forwardJump);
        setStartTime(new Date().getTime());
        setStep(step + 1);
        setDirection('left');
    };

    const onBackClick = () => {
        if (step === 0) return formContext.goBack();
        setStartTime(null);
        setStep(step - 1);
        setDirection('right');
    };

    const getPlanTargets = useCallback(() => {
        let plannedDrinks = drinksBaseline;
        let plannedDryDays = dryDaysBaseline;

        let taperNumber = 0;

        if (drinksBaseline <= 15) {
            taperNumber = Math.ceil(drinksBaseline * 0.1);
        } else if (drinksBaseline > 15 && drinksBaseline <= 30) {
            taperNumber = Math.floor(drinksBaseline * 0.13);
        } else if (drinksBaseline > 30) {
            if (concernLevelAnswer < 3) {
                taperNumber = Math.floor(drinksBaseline * 0.18);
            } else if (concernLevelAnswer >= 3) {
                taperNumber = Math.ceil(drinksBaseline * 0.2);
            }
        }

        plannedDrinks = drinksBaseline - taperNumber;

        if (dryDaysBaseline < 6 && drinkBaselines.some(drinks => drinks < 3)) {
            plannedDryDays = dryDaysBaseline + 1;
        }

        return {
            plannedDrinks,
            plannedDryDays 
        };

    }, [drinksBaseline, dryDaysBaseline]);

    const {
        plannedDrinks, plannedDryDays 
    } = getPlanTargets();

    useEffect(() => {
        if (!startTime) return;

        const endTime = new Date().getTime();
        const diff = endTime - startTime;
        const url = screens[step].props.trackingName;

        dataDogLogger(
            'info',
            `Diagnostic screen ${url} rendered in ${diff}ms`,
            {
                frontendMetric: diff,
                url,
                rawData: {
                    formId: formContext.formId
                }
            }
        );
    }, [startTime]);

    const screens = useMemo(() => [
        (
            <Pillar
                key={0}
                screenDirection={direction}
                slide
                title={'Planning & Goal Setting'}
                subtitle={'We\'ll start each week by setting drink and dry day goals for the week ahead, creating an accountability roadmap that adjusts to your progress.'}
                illustration={(
                    <Box
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            rowGap: '12px',
                            alignItems: 'center',
                            justifyContent: 'center' 
                        }}
                    >
                        <Box
                            style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'row',
                                columnGap: '12px',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '12px',
                                overflow: 'hidden' 
                            }}
                        >
                            <Box
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.primary.main,
                                    width: '100%',
                                    padding: '8px' 
                                }}
                            >
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    style={{
                                        width: '100px',
                                        fontWeight: TypographyWeights.SEMI_BOLD,
                                        color: theme.palette.white,
                                        textAlign: 'center'
                                    }}
                                >
                                    Your Starting Point
                                </Typography>
                            </Box>
                            <Box
                                style={{
                                    position: 'absolute',
                                    width: '30px',
                                    height: '30px',
                                    backgroundColor: theme.palette.primary['200'],
                                    border: `9px solid ${theme.palette.white}`,
                                    borderRadius: '50%' 
                                }}
                            />
                            <Box
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.primary.main,
                                    width: '100%',
                                    padding: '8px' 
                                }}
                            >
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    style={{
                                        width: '80px',
                                        fontWeight: TypographyWeights.SEMI_BOLD,
                                        color: theme.palette.white,
                                        textAlign: 'center'
                                    }}
                                >
                                    {'Next Week\'s Plan'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                columnGap: '32px',
                                alignItems: 'center',
                                justifyContent: 'center' 
                            }}
                        >
                            <Typography
                                variant={TypographyVariants.H1}
                                style={{
                                    fontWeight: TypographyWeights.BOLD,
                                    textAlign: 'center'
                                }}
                            >
                                {drinksBaseline}
                            </Typography>
                            <Box
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    rowGap: '5px',
                                    alignItems: 'center',
                                    justifyContent: 'center' 
                                }}
                            >
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    style={{
                                        textAlign: 'center'
                                    }}
                                >
                                    Weekly Total Drinks
                                </Typography>
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        columnGap: '2px',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: theme.palette.primary['300'],
                                        padding: '0 4px',
                                        borderRadius: '4px' 
                                    }}  
                                >
                                    <Typography
                                        variant={TypographyVariants.SMALL}
                                        style={{
                                            lineHeight: '20px',
                                            fontWeight: TypographyWeights.MEDIUM,
                                            textAlign: 'center',
                                            color: theme.palette.white
                                        }}
                                    >
                                        {plannedDrinks < drinksBaseline ? 'Reduce' : 'Maintain'}
                                    </Typography>
                                    {plannedDrinks < drinksBaseline ? (
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M3.73514 4.84313L9.8947 11.0027L6.79189 11.0027C5.90064 11.0027 5.90068 12.3395 6.79192 12.3396L9.61994 12.3374L11.5078 12.3359C11.8745 12.3348 12.1715 12.0378 12.1726 11.6711L12.1753 6.95616C12.1778 6.77441 12.1061 6.59934 11.9765 6.47185C11.552 6.04754 10.8269 6.35616 10.8384 6.95618L10.8421 10.0627L4.65444 3.87504C3.96846 3.25392 3.07346 4.24651 3.73514 4.84313Z"
                                                fill={theme.palette.white}
                                            />
                                        </svg>
                                    ) : (
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M2.69892 8.67584H11.4099L9.21584 10.8699C8.58563 11.5001 9.53095 12.4453 10.1612 11.8151L12.1593 9.81388L13.4932 8.47791C13.7517 8.21785 13.7517 7.79785 13.4932 7.53779L10.1612 4.20187C10.0344 4.0716 9.85989 3.99851 9.67808 4.00002C9.07795 4.0001 8.78343 4.73106 9.21584 5.14721L11.4151 7.34125H2.66441C1.74015 7.38711 1.80916 8.72185 2.69892 8.67584Z"
                                                fill={theme.palette.white}
                                            />
                                        </svg>
                                    )}
                                </Box>
                            </Box>
                            <Typography
                                variant={TypographyVariants.H1}
                                style={{
                                    fontWeight: TypographyWeights.BOLD,
                                    textAlign: 'center'
                                }}
                            >
                                {plannedDrinks}
                            </Typography>
                        </Box>
                        <Box
                            style={{
                                height: '1px',
                                width: '100%',
                                backgroundColor: theme.palette.neutral['100'],
                                margin: '12px 0 8px' 
                            }}
                        />
                        <Box
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                columnGap: '32px',
                                alignItems: 'center',
                                justifyContent: 'center' 
                            }}
                        >
                            <Typography
                                variant={TypographyVariants.H1}
                                style={{
                                    fontWeight: TypographyWeights.BOLD,
                                    textAlign: 'center'
                                }}
                            >
                                {dryDaysBaseline}
                            </Typography>
                            <Box
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    rowGap: '5px',
                                    alignItems: 'center',
                                    justifyContent: 'center' 
                                }}
                            >
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    style={{
                                        textAlign: 'center'
                                    }}
                                >
                                    Weekly Dry Days
                                </Typography>
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        columnGap: '2px',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: theme.palette.primary['300'],
                                        padding: '0 4px',
                                        borderRadius: '4px' 
                                    }}
                                >
                                    <Typography
                                        variant={TypographyVariants.SMALL}
                                        style={{
                                            lineHeight: '20px',
                                            fontWeight: TypographyWeights.MEDIUM,
                                            textAlign: 'center',
                                            color: theme.palette.white
                                        }}
                                    >
                                        {plannedDryDays > dryDaysBaseline ? 'Increase' : 'Maintain'}
                                    </Typography>
                                    {plannedDryDays > dryDaysBaseline ? (
                                        <svg
                                            width="17"
                                            height="16"
                                            viewBox="0 0 17 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M5.17492 12.1194L11.3345 5.95979L11.3345 9.06261C11.3345 9.95385 12.6713 9.95382 12.6713 9.06257L12.6691 6.23456L12.6677 4.34669C12.6666 3.98001 12.3696 3.68302 12.0029 3.68193L7.28795 3.67918C7.1062 3.67671 6.93112 3.74841 6.80364 3.87804C6.37933 4.30245 6.68795 5.02757 7.28797 5.01608L10.3945 5.01241L4.20683 11.2001C3.5857 11.886 4.5783 12.781 5.17492 12.1194Z"
                                                fill={theme.palette.white}
                                            />
                                        </svg>
                                    ) : (
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M2.69892 8.67584H11.4099L9.21584 10.8699C8.58563 11.5001 9.53095 12.4453 10.1612 11.8151L12.1593 9.81388L13.4932 8.47791C13.7517 8.21785 13.7517 7.79785 13.4932 7.53779L10.1612 4.20187C10.0344 4.0716 9.85989 3.99851 9.67808 4.00002C9.07795 4.0001 8.78343 4.73106 9.21584 5.14721L11.4151 7.34125H2.66441C1.74015 7.38711 1.80916 8.72185 2.69892 8.67584Z"
                                                fill={theme.palette.white}
                                            />
                                        </svg>
                                    )}
                                </Box>
                            </Box>
                            <Typography
                                variant={TypographyVariants.H1}
                                style={{
                                    fontWeight: TypographyWeights.BOLD,
                                    textAlign: 'center'
                                }}
                            >
                                {plannedDryDays}
                            </Typography>
                        </Box>
                    </Box>
                )}
                trackingName={'planning overview 1'}
            />
        ),
        (
            <Pillar
                key={1}
                screenDirection={direction}
                slide
                title={'Tracking & Daily Nudges'}
                subtitle={'With your plan in place, we\'ll build the habit of pausing to track each drink, and provide daily nudges to stay on pace with your goals.'}
                illustration={(
                    <Box className={classes.texts}>
                        <img
                            className={classes.text1}
                            src={'/img/sunnyside/diagnostic/text1.png'}
                            alt='Screenshot of text message communication between a Sunnyside coach and a Sunnyside member.'
                            width={311}
                            height={72}
                        />
                        <img
                            className={classes.text2}
                            src={'/img/sunnyside/diagnostic/text2.png'}
                            alt='Screenshot of text message communication between a Sunnyside coach and a Sunnyside member.'
                            width={154}
                            height={62}
                        />
                        <img
                            className={classes.text3}
                            src={'/img/sunnyside/diagnostic/text3.png'}
                            alt='Screenshot of text message communication between a Sunnyside coach and a Sunnyside member.'
                            width={311}
                            height={56}
                        />
                    </Box>
                )}
                trackingName={'tracking overview 1'}
            />
        ),
        (
            <Pillar
                key={2}
                screenDirection={direction}
                slide
                title={'Coaching'}
                subtitle={'Whenever you need a little extra love, you can text with a friendly human coach for advice, encouragement and support — available 7 days a week!'}
                illustration={(
                    <Box className={classes.coaches}>
                        <img
                            className={classes.hannah}
                            src={'/img/sunnyside/diagnostic/hannah.png'}
                            alt='Screenshot of text message communication between a Sunnyside coach and a Sunnyside member.'
                            width={150}
                            height={150}
                        />
                        <img
                            className={classes.ash}
                            src={'/img/sunnyside/diagnostic/ash.png'}
                            alt='Screenshot of text message communication between a Sunnyside coach and a Sunnyside member.'
                            width={124}
                            height={124}
                        />
                        <img
                            className={classes.cairo}
                            src={'/img/sunnyside/diagnostic/cairo.png'}
                            alt='Screenshot of text message communication between a Sunnyside coach and a Sunnyside member.'
                            width={138}
                            height={138}
                        />
                    </Box>
                )}
                trackingName={'coaching overview 1'}
            />
        ),
        (
            <Pillar
                key={3}
                screenDirection={direction}
                slide
                title={'Community Support'}
                subtitle={'With Sunnyside, you\'re never alone - engage anonymously with others who share your goals, providing an added layer of accountability and support.'}
                illustration={(
                    <Box
                        style={{
                            width: '343px',
                            height: '256px',
                            marginTop: '-16px' 
                        }}
                    >
                        <img
                            style={{
                                width: '343px',
                                height: '255px',
                                position: 'absolute',
                                transform: 'translate(0, 2px)'
                            }}
                            src={'/img/sunnyside/diagnostic/community.png'}
                            alt='Screenshot of a community post receiving hearts.'
                            width={343}
                            height={235}
                        />
                        <Box
                            style={{
                                position: 'absolute',
                                width: '343px',
                                height: '256px',
                                transform: 'translate(0, -86px)' 
                            }}
                        >
                            <LottieAnimation
                                fileName={'hearts.json'}
                                segments={[{
                                    frames: [0, 90] 
                                }]}
                            />
                        </Box>
                    </Box>
                )}
                trackingName={'community overview 1'}
            />
        )
    ], [direction, drinksBaseline, dryDaysBaseline]);

    const onClick = () => {
        advanceScreen();
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const resizeListener = () => {
            setScreenWidth(window.innerWidth);
        };
        window.addEventListener('resize', resizeListener);
        setStep(formContext.screenDirection === 'right' ? 3 : 0);

        return () => {
            window.removeEventListener('resize', resizeListener);
        };
    }, []);

    return (
        <CardContainer
            sectionKey={'plan'}
            goBack={onBackClick}
            goNext={onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position + step}
                    sectionKey={'plan'}
                />
                <Box className={classes.scrollableContainer}>
                    <Box>
                        {step >= 0 && (
                            <svg
                                width={Math.min(screenWidth * 4, 2768)}
                                height="60"
                                className={classes.progress}
                                style={{
                                    transform: `translate(${-Math.floor(step) * Math.min(screenWidth, 692) - 24}px, 0)` 
                                }}
                            >
                                <line
                                    className={formContext.screenDirection === 'left' ? classes.snake : null}
                                    x1={Math.min(screenWidth / 2, 346)}
                                    y1="30"
                                    x2={Math.min(screenWidth * 7 / 2, 2422)}
                                    y2="30"
                                    stroke={theme.palette.primary['300']}
                                    strokeWidth={'12px'}
                                    strokeDasharray={formContext.screenDirection === 'left' ? Math.min(screenWidth * 7 / 2, 2422) - Math.min(screenWidth / 2, 346) : 0}
                                    strokeDashoffset={formContext.screenDirection === 'left' ? Math.min(screenWidth * 7 / 2, 2422) - Math.min(screenWidth / 2, 346) : 0}
                                />
                                <g>
                                    <g>
                                        <circle
                                            cx={Math.min(screenWidth / 2, 346)}
                                            cy="30"
                                            r="30"
                                            fill={theme.palette.primary.main}
                                            stroke={theme.palette.white}
                                            strokeWidth={'4px'}
                                        />
                                        <g
                                            style={{
                                                transform: `translate(${(step === 0 ? 0 : Math.min(screenWidth, 692)) + (Math.min(screenWidth / 2, 346) - 11.7)}px, ${17.72}px)` 
                                            }}
                                        >
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g clipPath="url(#clip0_4414_15404)">
                                                    <path
                                                        d="M4.70045 0.722656C2.27017 0.722656 0.300049 2.69278 0.300049 5.12306V6.7732C0.300049 9.20348 2.27018 11.1736 4.70045 11.1736H6.3506C8.78087 11.1736 10.751 9.20347 10.751 6.7732V5.12305C10.751 2.69278 8.78087 0.722656 6.35059 0.722656H4.70045Z"
                                                        fill={theme.palette.white}
                                                    />
                                                    <path
                                                        d="M16.8008 12.8276C14.3705 12.8276 12.4004 14.7977 12.4004 17.228V18.8781C12.4004 21.3084 14.3705 23.2785 16.8008 23.2785H18.4509C20.8812 23.2785 22.8513 21.3084 22.8513 18.8781V17.228C22.8513 14.7977 20.8812 12.8276 18.4509 12.8276H16.8008Z"
                                                        fill={theme.palette.white}
                                                    />
                                                    <path
                                                        d="M4.70045 12.8276C2.27017 12.8276 0.300049 14.7977 0.300049 17.228V18.8781C0.300049 21.3084 2.27018 23.2785 4.70045 23.2785H6.3506C8.78087 23.2785 10.751 21.3084 10.751 18.8781V17.228C10.751 14.7977 8.78087 12.8276 6.35059 12.8276H4.70045Z"
                                                        fill={theme.palette.white}
                                                    />
                                                    <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M23.3707 2.40459C23.8098 2.84377 23.8098 3.55582 23.3707 3.995L18.6654 8.70029C17.787 9.57866 16.3629 9.57865 15.4845 8.70029L14.0796 7.2953C13.6404 6.85612 13.6404 6.14407 14.0796 5.70489C14.5187 5.2657 15.2308 5.2657 15.67 5.70489L17.075 7.10988L21.7802 2.40459C22.2194 1.96541 22.9315 1.96541 23.3707 2.40459Z"
                                                        fill={theme.palette.white}
                                                    />
                                                </g>
                                                <defs>
                                                    <clipPath id="clip0_4414_15404">
                                                        <rect
                                                            width="24"
                                                            height="24"
                                                            fill={theme.palette.white}
                                                        />
                                                    </clipPath>
                                                </defs>
                                            </svg>
                                        </g>
                                    </g>
                                    <g>
                                        <circle
                                            cx={Math.min(screenWidth * 3 / 2, 1038)}
                                            cy="30"
                                            r="30"
                                            fill={theme.palette.primary.main}
                                            stroke={theme.palette.white}
                                            strokeWidth={'4px'}
                                        />
                                        <g
                                            style={{
                                                transform: `translate(${(step <= 1 ? 0 : Math.min(screenWidth, 692)) + (Math.min(screenWidth * 3 / 2, 1038) - 16)}px, ${14}px)` 
                                            }}
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M0 14.5116C0 6.38935 7.27698 0 16 0C24.7231 0 32 6.38935 32 14.5116C32 19.0666 29.2489 22.9411 26.0537 25.8488C22.8394 28.7739 18.9991 30.8864 16.4145 31.9202C16.0707 32.0577 15.6809 32.0157 15.3741 31.808C15.0674 31.6003 14.8837 31.2541 14.8837 30.8837V28.2381C11.2638 27.9745 7.74811 26.4524 5.05281 24.2064C2.08697 21.7348 0 18.2827 0 14.5116ZM9.30233 16.7442C10.5353 16.7442 11.5349 15.7446 11.5349 14.5116C11.5349 13.2786 10.5353 12.2791 9.30233 12.2791C8.06931 12.2791 7.06977 13.2786 7.06977 14.5116C7.06977 15.7446 8.06931 16.7442 9.30233 16.7442ZM16 16.7442C17.233 16.7442 18.2326 15.7446 18.2326 14.5116C18.2326 13.2786 17.233 12.2791 16 12.2791C14.767 12.2791 13.7674 13.2786 13.7674 14.5116C13.7674 15.7446 14.767 16.7442 16 16.7442ZM24.9302 14.5116C24.9302 15.7446 23.9306 16.7442 22.6977 16.7442C21.4647 16.7442 20.4651 15.7446 20.4651 14.5116C20.4651 13.2786 21.4647 12.2791 22.6977 12.2791C23.9306 12.2791 24.9302 13.2786 24.9302 14.5116Z"
                                                fill={theme.palette.white}
                                            />
                                        </g>
                                    </g>
                                    <g>
                                        <circle
                                            cx={Math.min(screenWidth * 5 / 2, 1730)}
                                            cy="30"
                                            r="30"
                                            fill={theme.palette.primary.main}
                                            stroke={theme.palette.white}
                                            strokeWidth={'4px'}
                                        />
                                        <g
                                            style={{
                                                transform: `translate(${(step <= 2 ? 0 : Math.min(screenWidth, 692)) + (Math.min(screenWidth * 5 / 2, 1730) - 14)}px, ${12}px)` 
                                            }}
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M23.4825 0.722626H23.0949C23.2587 1.18768 23.3333 1.71279 23.3333 2.25254C23.3333 5.14759 20.9777 7.48713 18.115 7.48713H9.88486C7.00742 7.48713 4.68144 5.14759 4.68144 2.25254C4.68144 1.71279 4.75606 1.18768 4.92004 0.722626H4.51746C2.0275 0.722626 0 2.74762 0 5.25231V31.4403C0 33.9604 2.0275 36 4.51746 36H23.4825C25.9723 36 28 33.9604 28 31.4403V5.25231C28 2.74762 25.9723 0.722626 23.4825 0.722626ZM21.6485 28.9056H13.7764C12.9563 28.9056 12.3004 28.2457 12.3004 27.4207C12.3004 26.5957 12.9563 25.9208 13.7764 25.9208H21.6485C22.4537 25.9208 23.1245 26.5957 23.1245 27.4207C23.1245 28.2457 22.4537 28.9056 21.6485 28.9056ZM21.6485 19.9513H13.7764C12.9563 19.9513 12.3004 19.2914 12.3004 18.4664C12.3004 17.6414 12.9563 16.9665 13.7764 16.9665H21.6485C22.4537 16.9665 23.1245 17.6414 23.1245 18.4664C23.1245 19.2914 22.4537 19.9513 21.6485 19.9513ZM9.41691 27.4786C9.41691 28.7858 8.36354 29.8455 7.06414 29.8455C5.76474 29.8455 4.71137 28.7858 4.71137 27.4786C4.71137 26.1714 5.76474 25.1117 7.06414 25.1117C8.36354 25.1117 9.41691 26.1714 9.41691 27.4786ZM7.06414 20.7283C8.36354 20.7283 9.41691 19.6686 9.41691 18.3614C9.41691 17.0542 8.36354 15.9946 7.06414 15.9946C5.76474 15.9946 4.71137 17.0542 4.71137 18.3614C4.71137 19.6686 5.76474 20.7283 7.06414 20.7283Z"
                                                fill={theme.palette.white}
                                            />
                                            <path
                                                d="M20.3556 2.25356C20.3556 3.49817 19.3526 4.50713 18.1155 4.50713H9.88187C8.64467 4.50713 7.64173 3.49818 7.64173 2.25357C7.64173 1.00896 8.64467 0 9.88187 0H18.1155C19.3526 -1.49989e-07 20.3556 1.00895 20.3556 2.25356Z"
                                                fill={theme.palette.white}
                                            />
                                        </g>
                                    </g>
                                    <g>
                                        <circle
                                            cx={Math.min(screenWidth * 7 / 2, 2422)}
                                            cy="30"
                                            r="30"
                                            fill={theme.palette.primary.main}
                                            stroke={theme.palette.white}
                                            strokeWidth={'4px'}
                                        />
                                        <g
                                            style={{
                                                transform: `translate(${(step <= 3 ? 0 : Math.min(screenWidth, 692)) + (Math.min(screenWidth * 7 / 2, 2422) - 16)}px, ${16.53}px)` 
                                            }}
                                        >
                                            <path
                                                d="M21.4822 9.20745C21.1755 9.61065 20.7309 9.86989 20.229 9.93829C19.7276 10.0067 19.2295 9.87541 18.8263 9.56928L15.7742 7.2485L13.5385 5.49708C12.3684 4.5805 10.7285 4.56461 9.5409 5.45834L1.88945 11.2166C1.284 11.6722 0.410583 11.4137 0.243202 10.6746C0.0131948 9.6614 -0.00721345 8.92615 0.00161176 8.5781V8.54832C0.00106018 8.53563 0.000508607 8.51853 0.000508607 8.49868C0.00271491 7.39277 0.222242 6.31223 0.653574 5.2874C1.08215 4.26864 1.69771 3.35468 2.48425 2.57089C3.29396 1.76394 4.24819 1.13238 5.321 0.69333C6.44456 0.233316 7.6652 0 8.94872 0H8.97078C11.1688 0.0044126 13.1975 0.681748 15.1661 2.06786L15.1848 2.08102C15.2269 2.11072 15.2688 2.14017 15.3106 2.17045C15.3939 2.23057 15.4871 2.29841 15.5875 2.37288C15.7149 2.46609 15.8544 2.56924 16.0045 2.68066C17.8109 4.02154 21.0817 6.52238 21.1203 6.55216C21.9527 7.18427 22.1148 8.37567 21.4822 9.20745Z"
                                                fill={theme.palette.white}
                                            />
                                            <path
                                                d="M2.28442 12.9598L9.57402 7.64014C10.7493 6.78245 12.349 6.80092 13.5042 7.68553L18.1588 11.2499C19.7364 12.458 21.9947 12.1584 23.2028 10.5808C24.411 9.00312 24.1114 6.74481 22.5338 5.53669L17.8386 1.94124C17.6559 1.80137 17.6704 1.52205 17.8711 1.40954C19.8679 0.289892 21.733 0.0292469 23.033 0.0292469H23.065C24.3568 0.0320048 25.5841 0.270285 26.712 0.73747C27.7826 1.18094 28.7347 1.81635 29.5411 2.62662C30.3237 3.41371 30.936 4.32988 31.3602 5.35085C31.7871 6.37733 32.0022 7.45897 32 8.56377C32 8.58363 32 8.60128 31.9989 8.61342V8.6443C32.0061 8.9918 31.9829 9.72649 31.7485 10.7397C31.4274 12.1258 30.8323 13.5379 29.9807 14.9361C28.7843 16.9008 27.2504 18.598 25.2912 20.1253C22.9801 21.9268 21.6762 22.854 19.2261 24.4392C18.6999 24.7795 17.1587 25.7994 15.9591 26.5953C15.0672 27.1866 13.8934 27.0002 13.2282 26.1623C12.8802 25.7233 12.729 25.1761 12.803 24.6212C12.8769 24.0658 13.1659 23.5771 13.6171 23.2451L15.964 21.5164C16.1847 21.3543 16.2321 21.0437 16.0699 20.8231C15.9729 20.6907 15.8223 20.6201 15.6695 20.6201C15.5675 20.6201 15.4649 20.6515 15.3766 20.7161L11.2315 23.747C10.3225 24.4116 9.06714 24.2373 8.37381 23.3499C8.02246 22.9003 7.87408 22.3416 7.95572 21.7768C8.02466 21.2991 8.25026 20.8722 8.5994 20.5512L12.8394 17.501C13.0616 17.341 13.1124 17.031 12.953 16.8087C12.793 16.5859 12.4836 16.5351 12.2608 16.694L8.22488 19.5793C8.13718 19.6416 8.0539 19.7078 7.97447 19.7779L7.02245 20.4629C6.16254 21.0812 4.96618 20.9218 4.29877 20.0989C3.95128 19.6708 3.79794 19.1336 3.86689 18.5864C3.93639 18.0393 4.21824 17.5572 4.66116 17.229L5.0081 16.9725C5.06767 16.9345 5.12669 16.8937 5.1846 16.8506L9.72296 13.4789L9.72462 13.4778C9.94469 13.3139 9.99103 13.0034 9.82776 12.7833C9.66449 12.5632 9.35341 12.5169 9.13333 12.6802L4.4488 16.1512C3.63743 16.6482 2.57179 16.4811 1.95402 15.7348C1.61425 15.3239 1.46202 14.806 1.5249 14.2764C1.58778 13.7469 1.8575 13.2792 2.28442 12.9598Z"
                                                fill={theme.palette.white}
                                            />
                                        </g>
                                    </g>
                                </g>
                            </svg>
                        )}
                        {screens[step]}
                    </Box>
                    <Box
                        style={{
                            height: '300px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default Pillars2;
